/**
 * QUOTATION FILE MAPPERS — conversione DTO REST <-> UI per gli allegati di un
 * preventivo, più i builder del `FormData` per l'upload multipart.
 *
 * Pattern di riferimento: `quotationMappers.ts` dello stesso package.
 *
 * Il contenuto binario non viaggia mai nella response: si scarica dal
 * `fileEntryId` con le Headless DL API di Liferay. Il backend crea da sé il
 * `DLFileEntry` nella cartella `/Quotations/{quotationId}` della Document
 * Library.
 */

import { enumFromDto, pickAuditFields, type AuditFields } from '@crm/shared';
import { vocabularyKeyFromValue, vocabularyValueFromKey } from '../../plugin/vocabularyRegistry';

// ─── Types UI ──────────────────────────────────────────────────────────
//
// SYNC CROSS-LAYER — vedi il javadoc dell'enum Java:
//   QuotationFileType -> modules/crm-util/.../constants/QuotationFileType.java
//                        + locales/{it,en}.json (quotation.fileTypes.*)

/** I tipi del PRODOTTO. Chiuso di proposito: è la chiave della tabella di
 *  conversione, che deve avere una voce per ciascuno. */
export type CoreQuotationFileType = 'quotation' | 'attachment';

/**
 * Il tipo come lo vede la UI: quelli del prodotto più quelli che i plugin
 * abilitati contribuiscono al vocabolario (es. `dxf` di Trentino).
 *
 * L'unione è APERTA perché il vocabolario è estendibile. `(string & {})`
 * accetta qualunque stringa senza perdere l'autocompletamento sui valori noti:
 * chiuderla vorrebbe dire che il frontend può rappresentare solo i tipi che
 * conosceva a compile-time, cioè rinunciare all'estendibilita' proprio nel
 * punto in cui serve.
 */
export type QuotationFileType = CoreQuotationFileType | (string & {});

// ─── Tabelle di mapping ────────────────────────────────────────────────
// SYNC with QuotationFileType.java: QUOTATION=100, ATTACHMENT=200
// File: modules/crm-util/src/main/java/it/dvel/crm/util/constants/QuotationFileType.java
//
// Fascia 0-999 al core, una fascia da mille per ciascun plugin da 1000 in su,
// passo 100 dentro ogni fascia. I valori 1, 2 e 3 sono la numerazione pre-TS-87
// e restano RITIRATI: riassegnarli significherebbe che dei file storici, su un
// ambiente dove la migrazione fosse saltata, si ritroverebbero un tipo diverso
// da quello con cui sono stati caricati.
const QUOTATION_FILE_TYPE_TO_INT: Record<CoreQuotationFileType, number> = {
  quotation: 100,
  attachment: 200,
};
const QUOTATION_FILE_TYPE_FROM_INT: Record<number, CoreQuotationFileType> =
  invert(QUOTATION_FILE_TYPE_TO_INT);

// ─── API di mapping singolo enum ───────────────────────────────────────

/**
 * Al DTO: prima i tipi del prodotto, poi quelli contribuiti dai plugin.
 *
 * Un tipo sconosciuto a entrambi non è recuperabile - mandare zero
 * scriverebbe in colonna un valore che non significa niente - quindi si
 * solleva. Puo' capitare solo scrivendo a mano una stringa che nessuno
 * dichiara, cioè per un errore di battitura, ed è bene che si veda subito.
 */
const quotationFileTypeToDto = (v: QuotationFileType): number => {
  const core = QUOTATION_FILE_TYPE_TO_INT[v as CoreQuotationFileType];
  if (core !== undefined) return core;

  const contributed = vocabularyValueFromKey('quotationFileType', v);
  if (contributed !== undefined) return contributed;

  throw new Error(
    `[quotationFileType] "${v}" non è un tipo del prodotto né un tipo ` +
    'contribuito da un plugin abilitato.',
  );
};

/**
 * Dal DTO: prima i tipi del prodotto, poi il vocabolario contribuito dai
 * plugin, e solo alla fine il ripiego.
 *
 * L'ordine è il punto. Prima di questo controllo un DXF - valore 1000, fascia
 * di Trentino - cadeva su "allegato generico": etichetta sbagliata a video e
 * plugin incapace di riconoscere i propri file. Il ripiego resta per i valori
 * che nessuno dichiara, che sono dati vecchi o un plugin spento, e li' avvisare
 * in console e mostrare qualcosa è meglio che rompere la pagina.
 */
export const quotationFileTypeFromDto = (v: number): QuotationFileType => {
  const core = QUOTATION_FILE_TYPE_FROM_INT[v];
  if (core !== undefined) return core;

  const contributed = vocabularyKeyFromValue('quotationFileType', v);
  if (contributed !== undefined) return contributed;

  return enumFromDto('QuotationFileType', v, QUOTATION_FILE_TYPE_FROM_INT, 'attachment');
};

// ─── DTO shape (REST wire) ─────────────────────────────────────────────

/**
 * Shape del QuotationFile come esce dal REST
 * (`/o/crm/v1_0/quotation/{quotationId}/file/{quotationFileId}`). Tutti i
 * metadati tecnici — `extension`, `mimeType`, `size` — sono determinati dal
 * server: il client non li invia e non può sovrascriverli.
 */
export interface QuotationFileDto {
  quotationFileId: number;
  companyId: number;
  groupId: number;
  userId: number;
  userName: string;
  createDate: string;
  modifiedDate: string;
  quotationId: number;
  fileEntryId: number;
  fileName: string;
  extension: string;
  mimeType: string;
  size: number;
  type: number;
  /**
   * URL di preview/download. Non è nello schema OpenAPI — il client dovrebbe
   * costruirlo dal `fileEntryId` via Headless DL API — ma i mock MSW lo
   * popolano in base all'estensione, quindi il mapper lo legge e lo ripassa.
   */
  previewUrl?: string;
}

export interface QuotationFile extends AuditFields {
  quotationFileId: number;
  quotationId: number;
  fileEntryId: number;
  fileName: string;
  extension: string;
  mimeType: string;
  size: number;
  type: QuotationFileType;
  /** Vedi `QuotationFileDto.previewUrl`. Non serializzato verso il backend. */
  previewUrl?: string;
}

// ─── Whole-DTO mappers ────────────────────────────────────────────────

export function quotationFileFromDto(dto: QuotationFileDto): QuotationFile {
  return {
    quotationFileId: dto.quotationFileId,
    ...pickAuditFields(dto),
    quotationId: dto.quotationId,
    fileEntryId: dto.fileEntryId,
    fileName: dto.fileName,
    extension: dto.extension,
    mimeType: dto.mimeType,
    size: dto.size,
    type: quotationFileTypeFromDto(dto.type),
    previewUrl: dto.previewUrl,
  };
}

/** Modello UI -> DTO REST. Serve ai mock MSW, non all'app reale. */
export function quotationFileToDto(f: QuotationFile): QuotationFileDto {
  return {
    quotationFileId: f.quotationFileId,
    ...pickAuditFields(f),
    quotationId: f.quotationId,
    fileEntryId: f.fileEntryId,
    fileName: f.fileName,
    extension: f.extension,
    mimeType: f.mimeType,
    size: f.size,
    type: quotationFileTypeToDto(f.type),
    previewUrl: f.previewUrl,
  };
}

// ─── Multipart builders ────────────────────────────────────────────────
//
// Il body è `FormData` a runtime, quindi il payload multipart non ha un type TS
// che lo rappresenti: questi due builder SONO il contratto. I nomi dei campi
// sono quelli di `QuotationFileInput` nell'OpenAPI.
//
// `createHttpClient` riconosce `body instanceof FormData` e non imposta il
// `Content-Type`, lasciando che sia il browser a generarlo col boundary. Senza,
// il backend riceverebbe `application/json` con dentro del binario e
// risponderebbe 400.

/**
 * `POST /quotation/{quotationId}/file`. Tutti e tre i campi sono obbligatori.
 */
export function buildQuotationFileUploadFormData(payload: {
  type: QuotationFileType;
  fileName: string;
  file: File | Blob;
}): FormData {
  const fd = new FormData();
  fd.append('type', String(quotationFileTypeToDto(payload.type)));
  fd.append('fileName', payload.fileName);
  fd.append('file', payload.file);
  return fd;
}

/**
 * `PATCH /quotation/{quotationId}/file/{quotationFileId}`. `type` è sempre
 * obbligatorio; `fileName` e `file` sono opzionali, ma vanno forniti insieme
 * per sostituire il contenuto binario. Qui si aggiungono solo se definiti dal
 * chiamante: la coerenza la valida il backend.
 */
export function buildQuotationFileUpdateFormData(payload: {
  type: QuotationFileType;
  fileName?: string;
  file?: File | Blob;
}): FormData {
  const fd = new FormData();
  fd.append('type', String(quotationFileTypeToDto(payload.type)));
  if (payload.fileName !== undefined) fd.append('fileName', payload.fileName);
  if (payload.file !== undefined) fd.append('file', payload.file);
  return fd;
}

// ─── Helper ────────────────────────────────────────────────────────────

function invert<K extends string>(src: Record<K, number>): Record<number, K> {
  const out = {} as Record<number, K>;
  for (const key of Object.keys(src) as K[]) out[src[key]] = key;
  return out;
}
